import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { withTheme } from 'styled-components';
import { SVGIcon, MaterialIcon } from '@pearsontechnology/aero-components';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import importFileExtensions from 'constants/extensions';
import { 
  regexFileExtension, 
  KnowledgeTypes,
} from 'constants/knowledgeMap';
import mimeTypes from 'constants/mimeTypes';
import { getIcon } from 'Workspace/helpers';
import {
  DropzoneContainer,
  DragAreaText,
  DragAreaInput,
  DragAreaLink,
  DragAreaDiv,
  ErrorInfo,
  DroppedFileName,
  FileIcon,
  CancelButton,
  FileNameWrapper,
  ExpandButton,
  ValidationErrorTogglePanel,
  ValidationErrorIcon,
  ValidationErrorMsg,
  ValidationErrorLabel,
} from './styles';
import messages from './messages';

const KnowledgeMapImportFileDropZone = ({ fieldName, setFieldValue, disabled, error,
  knowledgeMapImportErrorMessageList, resetValidationErrorList }) => {
  const [importErrorMessage, setImportErrorMessage] = useState('');
  const [fileName, setFileName] = useState('');
  const [expandValidationErrorPanel, setExpandValidationErrorPanel] = useState(false);

  const validationError = useMemo(() => (knowledgeMapImportErrorMessageList && 
    knowledgeMapImportErrorMessageList.length) > 0, [knowledgeMapImportErrorMessageList]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length === 0) {
        setImportErrorMessage(intl.formatMessage(messages.wrongFileFormat));
        return;
      }
      const ext = regexFileExtension.exec(acceptedFiles[0].name)[1];
      if (ext === importFileExtensions.CSV || ext === importFileExtensions.XLSX){
        // Sets the appropriate value to the form object
        setImportErrorMessage('');
        setFieldValue(fieldName, acceptedFiles[0]);
        // uploading only one file at a time
        setFileName(acceptedFiles[0].name);
      } else {
        acceptedFiles.length = 0;
        // Reset the values
        setFieldValue(fieldName, undefined);
        setImportErrorMessage(intl.formatMessage(messages.wrongFileFormat));
      }
    },
    [fieldName, setFileName, setFieldValue]
  );

  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    noClick: true,
    accept: `${mimeTypes.CSV},${mimeTypes.XLSX},${mimeTypes.TXT},${mimeTypes.CSV_PLAIN}`,
    onDrop,
    multiple: false,
    disabled,
  });

  const removeFile = () => {
    acceptedFiles.length = 0;
    // Reset the values
    setFieldValue(fieldName, undefined);
    setFileName('');
    resetValidationErrorList();
  };

  const acceptedFilesLength = acceptedFiles.length;

  const droppedFileName = (
    <DroppedFileName>
      <FileNameWrapper>
        <span>
          <FileIcon icon="duotoneFile"></FileIcon>
        </span>
        {fileName}
      </FileNameWrapper>
      <CancelButton grey iconOnly disabled={disabled} onClick={removeFile}>
        <MaterialIcon id="delete" />
      </CancelButton>
    </DroppedFileName>
  );

  const dragAreaInfo = (
    <DragAreaDiv>
      <DragAreaText>
        {intl.formatMessage(messages.dragAndDropUploadMessage)}
      </DragAreaText>
      <DragAreaText>{intl.formatMessage(messages.or)}</DragAreaText>
      <DragAreaLink type="button" onClick={open}>
        {intl.formatMessage(messages.browseFile)}
      </DragAreaLink>
    </DragAreaDiv>
  );

  const toggleErrorPanel = (event) => {
    event.preventDefault(); 
    setExpandValidationErrorPanel(!expandValidationErrorPanel);
  };

  return (
    <>
      <DropzoneContainer
        error={error}
        validationError={validationError}
        acceptedFilesLength={acceptedFilesLength}
        {...getRootProps({
          isDragActive,
          isDragAccept,
          isDragReject,
          className: `dropzone ${disabled ? 'disabled' : ''}`,
        })}
      >
        <DragAreaInput {...getInputProps()} />
        {!acceptedFilesLength &&
          (!isDragActive || isDragReject) &&
          dragAreaInfo}
        {!!acceptedFilesLength && droppedFileName}
      </DropzoneContainer>
      <ErrorInfo>
        {(error || importErrorMessage || isDragReject) && 
        <SVGIcon icon={getIcon(KnowledgeTypes.WARNING_CIRCLE)} viewBox="0 0 24 24">
        </SVGIcon>}
        {isDragReject ? intl.formatMessage(messages.wrongFileFormat) : importErrorMessage}
      </ErrorInfo>
      {validationError && <ValidationErrorMsg>
        <ValidationErrorLabel>
          <ErrorInfo>
            {<SVGIcon icon={getIcon(KnowledgeTypes.WARNING_CIRCLE)} viewBox="0 0 24 24"></SVGIcon>}
            {intl.formatMessage(messages.importFailed)}
          </ErrorInfo>
        </ValidationErrorLabel>
        <ValidationErrorIcon>
          <ExpandButton
            expanded={expandValidationErrorPanel}
            onClick={toggleErrorPanel}
          />
        </ValidationErrorIcon>
      </ValidationErrorMsg>}
      {validationError && expandValidationErrorPanel && <ValidationErrorTogglePanel>
        {knowledgeMapImportErrorMessageList.map((item, index) => <div key={index}>{item}</div>)}
      </ValidationErrorTogglePanel>}
    </>
  );
};

KnowledgeMapImportFileDropZone.propTypes = {
  fieldName: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  knowledgeMapImportErrorMessageList: PropTypes.array,
  setFieldValue: PropTypes.func,
  resetValidationErrorList: PropTypes.func,

};
export default withTheme(KnowledgeMapImportFileDropZone);
